import React, { useEffect, useState, useMemo } from "react";
import { Pie, Column } from "@ant-design/plots";
import {
  convertUnitsWater,
  waterFormulasIndicators,
} from "../../../../utils/dataAndFormulas/waterForms";
import { convertUnitsFinal } from "../../../../utils/dataAndFormulas/effluentForms";
import { Waterform } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { convertUnits } from "../../../../utils/dataAndFormulas/convert";
import { useWindowWidth } from "../../../../hooks/useWindowWidth";
import { getDonutHeight, BAR_PALETTE, DONUT_COLORS } from "../../../../utils/chartConfig";
import { formatWhole, formatPercentage } from "../../../../utils/numberFormat";

import "./TableGraf.scss";
import { EmptyChartMessage } from "../EmptyChartMessage";

const waterController = new Waterform();

// --- Tipos de efluente (dona) ---
const effluentTypeGroups = {
  KPIefl1: ["domestic_effluents"],
  KPIefl2: ["industrial_effluents"],
  KPIefl3: ["combined_effluents"],
};

export const effluentTypeLabels = {
  KPIefl1: "domestico",
  KPIefl2: "industrial",
  KPIefl3: "combinada",
};

/**
 * Suma los valores de cada grupo (KPIefl1, KPIefl2, ...) para un año dado
 * — misma lógica que ya usaba TableGraf.js (getAnnualTotalsByGroup).
 */
export const getEffluentTypeTotals = (totalsPerYear, year) => {
  const results = {};
  Object.entries(effluentTypeGroups).forEach(([groupKey, conceptKeys]) => {
    let total = 0;
    let unit = "";
    conceptKeys.forEach((concept) => {
      const value = totalsPerYear?.[concept]?.[year]?.total || 0;
      const thisUnit = totalsPerYear?.[concept]?.[year]?.unit || "";
      if (!unit && thisUnit) unit = thisUnit;
      total += value;
    });
    results[groupKey] = { total, unit };
  });
  return results;
};

// Dona "Tipos de Efluentes" — misma lógica que ya usaba el useMemo de
// EffluentsDonut (convierte cada grupo a m3).
export const buildEffluentDonutData = (totalsPerYear) =>
  Object.entries(totalsPerYear)
    .filter(([, { total }]) => total > 0)
    .map(([groupKey, { total }]) => ({
      type: effluentTypeLabels[groupKey],
      value: convertUnitsFinal(total, totalsPerYear[groupKey]?.unit, "m3"),
      unit: "m3",
    }));

// --- "Efluentes vs Consumo de Agua": fetch + procesamiento propio ---
function calcularTotalesPorAño(documents) {
  const totalesAnuales = {};
  documents.forEach((doc) => {
    const year = String(doc.year);
    for (const code in doc) {
      if (["period", "year"].includes(code)) continue; // ignorar period y year
      const value = Number(doc[code]?.value) || 0;
      const unit = doc[code]?.unit || "";
      if (!totalesAnuales[code]) totalesAnuales[code] = {};
      if (!totalesAnuales[code][year]) totalesAnuales[code][year] = { total: 0, unit };
      totalesAnuales[code][year].total += value;
    }
  });
  return totalesAnuales;
}

function sumarValoresAgua(keys, year, data, unidadObjetivo = "m3") {
  let total = 0;
  for (const clave of keys) {
    const registro = data?.[clave]?.[year];
    if (!registro) continue;
    const valor = registro.total ?? 0;
    const unidad = registro.unit ?? unidadObjetivo;
    if (valor === null || valor === undefined || isNaN(valor)) continue;
    const valorConvertido = convertUnits(valor, unidad, unidadObjetivo) || 0;
    total += valorConvertido;
  }
  return total;
}

const waterCodes = {
  production: [
    "municipal_network_water_production",
    "groundwater_production",
    "rainwater",
    "surface_water",
  ],
  domestic: ["water_domestic_municipal_network", "domestic_groundwater"],
  combined: ["combined_municipal_network_water", "groundwater_combined"],
};

function calcularIndicadoresAguaPorAño(data, years) {
  const resultados = [];
  years.forEach((year) => {
    const totalProduccion = sumarValoresAgua(waterCodes.production, year, data);
    const totalDomestico = sumarValoresAgua(waterCodes.domestic, year, data);
    const totalCombinado = sumarValoresAgua(waterCodes.combined, year, data);
    resultados.push({
      year,
      total_water_production_kpis: totalProduccion, // Consumo total agua producción
      total_domestic_water_kpis: totalDomestico, // Consumo total agua doméstica
      combined_total_water_kpis: totalCombinado, // Consumo total agua combinada
    });
  });
  return resultados;
}

// Fetch + procesamiento de datos de Agua para "Efluentes vs Consumo de
// Agua" — misma lógica que ya usaba el useEffect de TableGraf, envuelta en
// una función para poder reusarla también desde el export a PDF
// (pdfEffluentCharts.js).
export async function fetchEffluentWaterIndicators(waterCtrl, accessToken, site, year) {
  const yearsCombined = [String(year), String(year - 1)];
  try {
    const responseWater = await waterCtrl.getWaterFormsByLastYears(accessToken, site, yearsCombined);
    const dataRaw = responseWater.waterForms || {};
    const totalesPorAño = calcularTotalesPorAño(dataRaw.data);
    return calcularIndicadoresAguaPorAño(totalesPorAño, yearsCombined);
  } catch (error) {
    console.error("Error al obtener o procesar datos de agua:", error);
    return [];
  }
}

// "Efluentes vs Consumo de Agua" — mismo cálculo que ya usaba el
// EffluentWaterBarChart en pantalla (buildEffluentAndWaterData), combina
// los totales de efluentes (de `totales`, como todo lo demás) con los
// indicadores de agua (del fetch propio, ver fetchEffluentWaterIndicators).
export const buildEffluentWaterData = (effluentData, waterIndicators) => {
  if (!effluentData && !waterIndicators) return [];

  // 🔹 Reunir todos los años presentes en ambos conjuntos
  const allYears = new Set([
    ...Object.keys(effluentData?.domestic_effluents || {}),
    ...Object.keys(effluentData?.industrial_effluents || {}),
    ...Object.keys(effluentData?.combined_effluents || {}),
    ...(waterIndicators?.map((d) => String(d.year)) || []),
  ]);

  // Ordenar descendente y tomar solo los últimos 5 años
  const years = [...allYears].sort((a, b) => b - a).slice(0, 5);

  const chartData = [];

  years.forEach((yearStr) => {
    const year = String(yearStr); // aseguramos tipo string

    // --- EFLUENTES ---
    const unitEfl =
      effluentData?.domestic_effluents?.[year]?.unit ||
      effluentData?.industrial_effluents?.[year]?.unit ||
      effluentData?.combined_effluents?.[year]?.unit ||
      "m3";

    const domestic = effluentData?.domestic_effluents?.[year]?.total || 0;
    const industrial = effluentData?.industrial_effluents?.[year]?.total || 0;
    const combined = effluentData?.combined_effluents?.[year]?.total || 0;

    // Solo agregar si el valor es mayor a cero
    if (domestic > 0)
      chartData.push({
        year,
        type: "Efluente Doméstico",
        value: convertUnitsFinal(domestic, unitEfl, "m3"),
        unit: "m3",
      });

    if (industrial > 0)
      chartData.push({
        year,
        type: "Efluente Industrial",
        value: convertUnitsFinal(industrial, unitEfl, "m3"),
        unit: "m3",
      });

    if (!domestic && !industrial && combined > 0)
      chartData.push({
        year,
        type: "Efluente Combinado",
        value: convertUnitsFinal(combined, unitEfl, "m3"),
        unit: "m3",
      });

    // --- AGUA (KPI) ---
    const agua = waterIndicators?.find((w) => String(w.year) === year);
    if (agua) {
      // Solo agregar si el valor es mayor a cero
      if (agua.total_water_production_kpis > 0)
        chartData.push({
          year,
          type: "Consumo Agua Producción",
          value: agua.total_water_production_kpis,
          unit: "m3",
        });
      if (agua.total_domestic_water_kpis > 0)
        chartData.push({
          year,
          type: "Consumo Agua Doméstica",
          value: agua.total_domestic_water_kpis,
          unit: "m3",
        });
      if (agua.combined_total_water_kpis > 0)
        chartData.push({
          year,
          type: "Consumo Agua Combinada",
          value: agua.combined_total_water_kpis,
          unit: "m3",
        });
    }
  });

  // Ordenar años numéricamente (aunque se guarden como string)
  return chartData.sort((a, b) => {
    if (a.year !== b.year) return a.year - b.year;

    const orden = {
      "Consumo Agua Producción": 1,
      "Efluente Industrial": 2,
      "Consumo Agua Doméstica": 3,
      "Efluente Doméstico": 4,
      "Consumo Agua Combinada": 5,
      "Efluente Combinado": 6,
    };
    return (orden[a.type] || 99) - (orden[b.type] || 99);
  });
};

// "Efluentes anuales" — mismo cálculo que ya usaba el useMemo de
// EffluentBarChart (ex buildEffluentTotalsByYear). Devuelve {year, total,
// unit} (no {year, value}) — el caller lo adapta según haga falta.
export const buildEffluentBarChartData = (data) => {
  if (!data) return [];

  const allYears = new Set([
    ...Object.keys(data.domestic_effluents || {}),
    ...Object.keys(data.industrial_effluents || {}),
    ...Object.keys(data.combined_effluents || {}),
  ]);

  // Ordenar descendente y tomar solo los últimos 5 años
  const years = [...allYears].sort((a, b) => b - a).slice(0, 5);

  const chartData = years.map((year) => {
    const domestic = data.domestic_effluents?.[year]?.total || 0;
    const domesticUnit = data.domestic_effluents?.[year]?.unit || null;
    const industrial = data.industrial_effluents?.[year]?.total || 0;
    const industrialUnit = data.industrial_effluents?.[year]?.unit || null;
    const combined = data.combined_effluents?.[year]?.total || 0;
    const combinedUnit = data.combined_effluents?.[year]?.unit || null;

    let total = 0;
    let unit =
      data.domestic_effluents?.[year]?.unit ||
      data.industrial_effluents?.[year]?.unit ||
      data.combined_effluents?.[year]?.unit ||
      "m3";

    if (domestic || industrial) {
      total =
        convertUnitsFinal(domestic, domesticUnit, "m3") +
        convertUnitsFinal(industrial, industrialUnit, "m3");
    } else {
      total = convertUnitsFinal(combined, combinedUnit, "m3");
    }

    return {
      year,
      total,
      unit,
    };
  });

  // Ordenar por año ascendente
  return chartData.sort((a, b) => a.year - b.year);
};

const TableGraf = ({ filters, totales, t, site }) => {
  const { year } = filters || {};
  const [dataConsumo, setDataConsumo] = useState([]);
  const [dataCosto, setDataCosto] = useState([]);
  const [dataWater, setDataWater] = useState([]);
  const windowWidth = useWindowWidth();

  const { accessToken } = useAuth();

  // Memoizar configuración responsive para evitar recalcular en cada render
  const responsiveConfig = useMemo(() => {
    const isMobile = windowWidth <= 768;
    const isSmallMobile = windowWidth <= 480;
    const isTablet = windowWidth > 768 && windowWidth <= 992;
    const isNotebook = windowWidth >= 1024 && windowWidth <= 1500;

    return {
      isMobile,
      isSmallMobile,
      isTablet,
      isNotebook,
      // Tamaños de fuente consistentes
      fontSize: {
        large: isSmallMobile ? 12 : isMobile ? 14 : 16,
        medium: isSmallMobile ? 10 : isMobile ? 11 : 12,
        small: isSmallMobile ? 9 : isMobile ? 10 : 11,
      },
      // Alturas de gráficos
      chartHeight: {
        donut: getDonutHeight(isNotebook, isMobile),
        bar: isSmallMobile ? 250 : isMobile ? 280 : 300,
      },
    };
  }, [windowWidth]);
  useEffect(() => {
    if (!totales || !year) return;

    const fetchData = async () => {
      const consumoData = getEffluentTypeTotals(totales, [filters.year]);
      const costoData = totales;

      setDataConsumo(consumoData);
      setDataCosto(costoData);

      const indicadores = await fetchEffluentWaterIndicators(waterController, accessToken, site, year);
      setDataWater(indicadores);
    };

    fetchData();
  }, [filters, totales, year]);

  const effluentColorMapDonut = DONUT_COLORS.effluent;

  const effluentWaterColorMap = DONUT_COLORS.effluentWater;

  const barPalette = BAR_PALETTE;

  const EffluentsDonut = ({ totalsPerYear }) => {
    const [hiddenTypes, setHiddenTypes] = useState([]);

    const allData = useMemo(() => buildEffluentDonutData(totalsPerYear), [totalsPerYear]);

    const chartData = useMemo(() => {
      const visible = allData.filter(d => !hiddenTypes.includes(d.type));
      const totalVisible = visible.reduce((sum, d) => sum + d.value, 0);
      return visible.map(d => ({
        ...d,
        percent: totalVisible ? (d.value / totalVisible) * 100 : 0,
      }));
    }, [allData, hiddenTypes]);

    const totalVisible = chartData.reduce((sum, d) => sum + d.value, 0);

    const config = {
      data: chartData,
      angleField: "value",
      colorField: "type",
      scale: {
        color: {
          domain: Object.keys(effluentColorMapDonut),
          range: Object.values(effluentColorMapDonut),
        },
      },
      radius: 1,
      innerRadius: 0.6,
      height: responsiveConfig.chartHeight.donut,
      label: false,
      legend: false,
      statistic: {
        title: false,
        content: {
          content: `Total\n${formatWhole(totalVisible)}`,
          style: {
            fontSize: responsiveConfig.fontSize.large,
            fontWeight: 300,
            whiteSpace: "pre-line",
          },
        },
      },
      tooltip: {
        shared: true,
        title: (d) => t(d.type),
        items: [
          (datum) => ({
            name: t("value"),
            value: `${formatWhole(datum.value)} m³ (${formatPercentage(datum.percent || 0)})`,
          }),
        ],
      },
      interactions: [{ type: "element-active" }],
    };

    if (!allData.length) {
      return (
        <EmptyChartMessage
          message={
            t("complete_at_least_one_period") ||
            "Complete al menos un período para visualizar este indicador"
          }
        />
      );
    }

    return (
      <>
        <div className="chartWrapper">
          <Pie {...config} />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            alignItems: "start",
            gap: "8px",
            marginTop: "15px",
            borderTop: "1px solid #000",
            paddingTop: "15px",
            // 130px = mismo min-height que .pillsRow (tableGraf.shared.scss),
            // para que la línea (borderTop) quede a la misma altura que en
            // las cards de barras dentro del mismo chartCard flex-column.
            minHeight: "130px",
          }}
        >
          {allData.map((item) => {
            const isHidden = hiddenTypes.includes(item.type);
            const color = effluentColorMapDonut[item.type] || "#D9D9D9";
            return (
              <div
                key={item.type}
                onClick={() =>
                  setHiddenTypes(prev =>
                    prev.includes(item.type)
                      ? prev.filter(t => t !== item.type)
                      : [...prev, item.type]
                  )
                }
                className="yearPill"
                style={{
                  backgroundColor: isHidden ? "#E5E7EB" : color,
                  color: isHidden ? "#9CA3AF" : "#FFF",
                  padding: "6px 4px",
                  borderRadius: "20px",
                  fontSize: "11px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  border: `1px solid ${isHidden ? "#D1D5DB" : color}`,
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "28px",
                  opacity: isHidden ? 0.7 : 1,
                  textDecoration: isHidden ? "line-through" : "none",
                  userSelect: "none",
                }}
              >
                {item.type}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  /////////////Barras consumo
  const buildEffluentStackedData = (data) => {
    if (!data) return [];

    const years = new Set([
      ...Object.keys(data.domestic_effluents || {}),
      ...Object.keys(data.industrial_effluents || {}),
      ...Object.keys(data.combined_effluents || {}),
    ]);

    const chartData = [];

    years.forEach((year) => {
      const domestic = data.domestic_effluents?.[year]?.total || 0;
      const industrial = data.industrial_effluents?.[year]?.total || 0;
      const combined = data.combined_effluents?.[year]?.total || 0;

      const unit =
        data.domestic_effluents?.[year]?.unit ||
        data.industrial_effluents?.[year]?.unit ||
        data.combined_effluents?.[year]?.unit ||
        "m3";

      if (domestic || industrial) {
        // Si hay domestic/industrial, agregamos por separado (solo si > 0)
        if (domestic > 0) {
          chartData.push({
            year,
            type: "Doméstico",
            value: convertUnitsFinal(domestic, unit, "m3"),
            unit: "m3",
          });
        }
        if (industrial > 0) {
          chartData.push({
            year,
            type: "Industrial",
            value: convertUnitsFinal(industrial, unit, "m3"),
            unit: "m3",
          });
        }
      } else if (combined > 0) {
        // Si solo hay combined y es mayor a 0
        chartData.push({
          year,
          type: "Combinado",
          value: convertUnitsFinal(combined, unit, years, "m3"),
          unit: "m3",
        });
      }
    });

    return chartData.sort((a, b) => a.year - b.year);
  };

  const EffluentSeparatedBarChart = ({ data }) => {
    const chartData = buildEffluentStackedData(data);

    const config = {
      data: chartData,
      xField: "year",
      yField: "value",
      seriesField: "type", // barras por tipo
      isGroup: true, // asegura que no se apilen
      autoFit: true,
      height: 300,
      colorField: "type",
      barWidthRatio: 0.5, // 0.5 = más delgada
      marginRatio: 0.3, // espacio relativo entre grupos de barras
      label: {
        position: "top",
        text: ({ value, unit }) => `${value} ${unit}`,
        style: { fontSize: 12 },
      },
      tooltip: {
        shared: true,
        showMarkers: false,
      },
      xAxis: {
        title: { text: "Año" },
      },
      yAxis: {
        title: { text: "Total Energía" },
      },
      tooltip: {
        title: { field: "type" },
        items: [
          (datum) => ({
            name: t("value"),
            value: datum.value.toFixed(2),
          }),
        ],
      },
      interactions: [{ type: "active-region" }],
    };

    return <Column {...config} />;
  };

  const EffluentWaterBarChart = ({ effluentData, waterIndicators }) => {
    const rawData = buildEffluentWaterData(effluentData, waterIndicators);
    const allTypes = useMemo(() => [...new Set(rawData.map(d => d.type))], [rawData]);
    const [activeTypes, setActiveTypes] = useState(allTypes);

    const maxValue = Math.max(...rawData.map((d) => d.value));
    const visibleMaxValue = Math.max(...rawData.filter(d => activeTypes.includes(d.type)).map((d) => d.value));

    const chartData = rawData
      .filter(d => activeTypes.includes(d.type))
      .map((d) => {
        const isSmall = visibleMaxValue > 0 && d.value < visibleMaxValue * 0.1;
        return {
          year: d.year,
          type: d.type,
          unit: d.unit,
          displayValue: isSmall ? visibleMaxValue * 0.1 : d.value,
          realValue: d.value,
          isSmall,
        };
      });

    const config = {
      data: chartData,
      xField: "year",
      yField: "displayValue",
      // `group: true` (API v2) en vez de `seriesField`/`isGroup` (API
      // vieja v1) — mezclarla con `colorField`/`scale`/`axis` (v2) hacía
      // que axis.x/axis.y de más abajo se ignorara en silencio (mismo bug
      // ya visto en Residuos).
      group: true,
      autoFit: true,
      colorField: "type",
      scale: {
        color: {
          domain: Object.keys(effluentWaterColorMap),
          range: Object.values(effluentWaterColorMap),
        },
      },
      height: responsiveConfig.chartHeight.bar,
      barWidthRatio: 0.5,
      marginRatio: 0.3,
      label: false,
      legend: false,
      // `xAxis`/`yAxis` es la API vieja (v1) de @ant-design/plots — la
      // instalada (v2) no la reconoce, quedaba ignorada en silencio (mismo
      // bug ya visto en GEI/Social/Combustibles). axis.x/axis.y es la
      // forma correcta.
      axis: {
        x: {
          title: t("year"),
          titleFontSize: responsiveConfig.fontSize.medium,
          labelFontSize: responsiveConfig.fontSize.medium,
          labelAutoRotate: !responsiveConfig.isMobile,
          labelAutoHide: false,
        },
        y: {
          title: t("volume_m3_axis"),
          titleFontSize: responsiveConfig.fontSize.medium,
          labelFontSize: responsiveConfig.fontSize.small,
          labelFormatter: (v) => formatWhole(v),
        },
      },
      tooltip: {
        shared: true,
        showMarkers: false,
        title: "Año",
        items: [
          (datum) => ({
            name: datum.type,
            value: `${formatWhole(datum?.realValue ?? datum?.value ?? 0)} ${datum?.unit || ""}`,
          }),
        ],
      },
    };

    return (
      <>
        <div className="chartWrapper"><Column {...config} /></div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            alignItems: "start",
            gap: "8px",
            marginTop: "15px",
            borderTop: "1px solid #000",
            paddingTop: "15px",
            // 130px = mismo min-height que .pillsRow (tableGraf.shared.scss),
            // para que la línea (borderTop) quede a la misma altura que en
            // las cards de barras dentro del mismo chartCard flex-column.
            minHeight: "130px",
          }}
        >
          {allTypes.map((type) => {
            const isHidden = !activeTypes.includes(type);
            const color = effluentWaterColorMap[type] || "#D9D9D9";
            return (
              <div
                key={type}
                onClick={() =>
                  setActiveTypes(prev =>
                    prev.includes(type)
                      ? prev.filter(t => t !== type)
                      : [...prev, type]
                  )
                }
                className="yearPill"
                style={{
                  backgroundColor: isHidden ? "#E5E7EB" : color,
                  color: isHidden ? "#9CA3AF" : "#FFF",
                  padding: "6px 4px",
                  borderRadius: "20px",
                  fontSize: "10px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  border: `1px solid ${isHidden ? "#D1D5DB" : color}`,
                  textAlign: "center",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "28px",
                  opacity: isHidden ? 0.7 : 1,
                  textDecoration: isHidden ? "line-through" : "none",
                  userSelect: "none",
                }}
              >
                {type}
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const EffluentBarChart = ({ data, t }) => {
    const rawData = buildEffluentBarChartData(data);
    const allYears = rawData.map(d => d.year);
    const [activeYears, setActiveYears] = useState(allYears);

    const colorMap = useMemo(() => {
      const map = {};
      allYears.forEach((y, i) => { map[y] = barPalette[i] || "#ccc"; });
      return map;
    }, [allYears]);

    const maxValue = Math.max(...rawData.map((d) => d.total));

    const chartData = rawData
      .filter(d => activeYears.includes(d.year))
      .map((d) => {
        const isSmall = d.total < 1;
        return {
          year: d.year,
          unit: d.unit,
          displayValue: isSmall ? maxValue * 0.1 : d.total,
          realValue: d.total,
          isSmall,
        };
      });

    const config = {
      data: chartData,
      xField: "year",
      yField: "displayValue",
      colorField: "year",
      scale: { color: { domain: Object.keys(colorMap), range: Object.values(colorMap) } },
      autoFit: true,
      height: responsiveConfig.chartHeight.bar,
      label: false,
      legend: false,
      tooltip: {
        title: { field: "year" },
        items: [
          (datum) => {
            const value = datum?.realValue ?? datum?.total ?? 0;
            return { name: t("value"), value: `${formatWhole(value)} ${datum?.unit || ""}` };
          },
        ],
      },
      // Ver comentario en EffluentWaterBarChart más arriba: axis.x/axis.y
      // es la API correcta (v2), xAxis/yAxis se ignoraba en silencio.
      axis: {
        x: {
          title: t("year"),
          titleFontSize: responsiveConfig.fontSize.medium,
          labelFontSize: responsiveConfig.fontSize.medium,
          labelAutoRotate: !responsiveConfig.isMobile,
          labelAutoHide: false,
        },
        y: {
          title: responsiveConfig.isMobile ? t("total") : t("total_effluents_axis"),
          titleFontSize: responsiveConfig.fontSize.medium,
          labelFontSize: responsiveConfig.fontSize.small,
          labelFormatter: (v) => formatWhole(v),
        },
      },
      interactions: [{ type: "active-region" }],
    };

    return (
      <>
        <div className="chartWrapper"><Column {...config} /></div>
        <div className="pillsRow">
          {allYears.map(year => (
            <div
              key={year}
              className="yearPill"
              onClick={() => setActiveYears(prev =>
                prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]
              )}
              style={{
                backgroundColor: activeYears.includes(year) ? colorMap[year] : "#E5E7EB",
                color: activeYears.includes(year) ? "white" : "#9CA3AF",
                border: `1px solid ${activeYears.includes(year) ? colorMap[year] : "#D1D5DB"}`,
                opacity: activeYears.includes(year) ? 1 : 0.7,
                textDecoration: activeYears.includes(year) ? "none" : "line-through",
                cursor: "pointer",
                transition: "all 0.2s ease",
                userSelect: "none",
              }}
            >
              {year}
            </div>
          ))}
        </div>
      </>
    );
  };

  // ======================
  // 🎨 RENDER
  // ======================

  return (
    <div className="effluentDashboardGrid">
      <div className="chartCard">
        <h3 className="cardTitle">{t("effluents_vs_water_consumption")}</h3>
        <EffluentWaterBarChart effluentData={dataCosto} waterIndicators={dataWater} />
      </div>

      <div className="chartCard">
        <h3 className="cardTitle">{t("annual_effluents")}</h3>
        <EffluentBarChart data={dataCosto} t={t} />
      </div>

      <div className="chartCard">
        <h3 className="cardTitle">{t("types_effluents")}</h3>
        <EffluentsDonut totalsPerYear={dataConsumo} />
      </div>
    </div>
  );
};

export default TableGraf;
